//api.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Contact } from '../models/contact';
import { Appointment } from '../models/appointment';
import { Property } from '../models/property';
import { Invoice } from '../models/invoice';
import { Quote } from '../models/quote';
import { Business } from '../models/business';
import { WorkItem } from '../models/work-item';
import { Job } from '../models/job';
import { Payment } from '../models/payment';
import { Reminder } from '../models/reminder';
import { BusinessPaymentDetails } from '../models/business-payment-details';
import { Observable, throwError, BehaviorSubject, forkJoin, of } from 'rxjs';
import { retry, catchError, map, tap, switchMap } from 'rxjs/operators';

import { environment } from '../../environments/environment';

import { InvoiceService } from '../services/invoice.service';
import { QuoteService } from '../services/quote.service';
import { JobService } from '../services/job.service';
import { AppointmentService } from '../services/appointment.service';
import { StripeConnectAccountResponse, StripeConnectOnboardingResponse } from '../models/stripe-connect';
import { User } from '../models/user';
import { Feature, AccountFeature } from '../models/feature';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  // API path
  //base_path = 'http://192.168.86.29:3000/';
  base_path = environment.API_URL;

  constructor(
    private http: HttpClient,
    private invoiceService: InvoiceService,
    private quoteService: QuoteService,
    private jobService: JobService,
    private appointmentService: AppointmentService) { }

  // Http Options
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    })
  }

  noAccountHttpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Skip-Account-Interceptor': 'true'
    })
  }

  // Handle API errors
  private handleError(error: any) {
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
        // Client-side errors
        errorMessage = `Error: ${error.error.message}`;
    } else {
        // Server-side errors
        errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    console.error(errorMessage);
    return throwError(errorMessage);
  }

  /* Setup checklist */
  getSetupChecklist(accountId: number | string): Observable<any> {
    return this.http.get<any>(`${this.base_path}accounts/${accountId}/setupchecklist`)
      .pipe(retry(2), catchError(this.handleError));
  }

  /* Features */
  getFeatures(): Observable<Feature[]> {
    return this.http.get<Feature[]>(`${this.base_path}features`, this.noAccountHttpOptions)
      .pipe(
        map(list => list.map(item => new Feature().deserialize(item))),
        retry(2),
        catchError(this.handleError)
      );
  }

  setAccountFeatures(features: { featureId: number; enabled: boolean }[]): Observable<AccountFeature[]> {
    return this.http.post<AccountFeature[]>(`${this.base_path}features`, JSON.stringify({ features }), this.httpOptions)
      .pipe(retry(2), catchError(this.handleError));
  }

  // Phone verification
  verifyPhoneCode(code: string): Observable<User> {
    return this.http.post<User>(`${this.base_path}auth/verify-phone-code`, JSON.stringify({ code }), this.httpOptions)
      .pipe(retry(2), catchError(this.handleError));
  }

  /* 
   * CONTACTS
   */

  // Create a new contact
  createContact(item): Observable<Contact> {
    return this.http
      .post<Contact>(this.base_path+'contacts', JSON.stringify(item), this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  // Get single contact data by ID
  getContact(id): Observable<Contact> {
    return this.http
      .get<Contact>(this.base_path+'contacts/' + id)
      .pipe(
        map(data => new Contact().deserialize(data)),
        retry(2),
        catchError(this.handleError)
      )
  }

  // Get contacts data
  getContactList(): Observable<Contact[]> {
    return this.http
      .get<Contact[]>(this.base_path+'contacts')
      .pipe(
        map(data => data.map(data => new Contact().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      );
  }

  // Get contacts data - filtered
  getContactListFiltered(searchText): Observable<Contact[]> {
    return this.http
      .get<Contact[]>(this.base_path+'contacts'+'?query='+searchText)
      .pipe(
        map(data => data.map(data => new Contact().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      );
  } 

  // Update item by id
  updateContact(id, item): Observable<Contact> {
    return this.http
      .patch<Contact>(this.base_path + 'contacts/' + id, JSON.stringify(item), this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  // Delete item by id
  deleteContact(id) {
    return this.http
      .delete<Contact>(this.base_path + 'contacts/' + id, this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  /* 
   * PROPERTIES
   */

  // Create a new property
  createProperty(item): Observable<Property> {
    return this.http
      .post<Property>(this.base_path+'properties', JSON.stringify(item), this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  // Delete property by ID
  deleteProperty(id) {
    return this.http
      .delete<Property>(this.base_path + 'properties/' + id, this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  // Update property by ID
  updateProperty(id, item): Observable<Property> {
    return this.http
      .patch<Property>(this.base_path+'properties/' + id, JSON.stringify(item), this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  // Get single contact data by ID
  getProperty(id): Observable<Property> {
    return this.http
      .get<Property>(this.base_path+'properties/' + id)
      .pipe(
        map(data => new Property().deserialize(data)),
        retry(2),
        catchError(this.handleError)
      )
  }

  // Get contacts data
  getPropertyListByContact(contactId): Observable<Property[]> {
    return this.http
      .get<Property[]>(this.base_path+'properties?contactId='+contactId)
      .pipe(
        map(data => data.map(data => new Property().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      );
  }

  getPropertyListByContactFiltered(contactId, searchText): Observable<Property[]> {
    return this.http
      .get<Property[]>(this.base_path+'properties?contactId='+contactId+'&address1_like='+searchText)
      .pipe(
        map(data => data.map(data => new Property().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      );
  }

  /* 
   * INVOICES
   */

  getInvoicesMaster(page: number, limit: number, tab?: string, query?: string): Observable<Invoice[]> {
    let filter = '';
    
    // Handle tab-based filters
    if (tab) {
      switch (tab) {
        case 'all':
          filter += ''; // No additional filter needed
          break;
        case 'draft':
          filter += '&isPaid=false&isSent=false';
          break;
        case 'awaiting':
          filter += '&isPaid=false&isSent=true';
          break;
        case 'paid':
          filter += '&isPaid=true';
          break;
      }
    }
    
    // Handle search query
    if (query) {
      filter += `&query=${encodeURIComponent(query)}`; // Ensure query is URL encoded
    }

    const url = `${this.base_path}invoices?sort=invoice_date&order=DESC&page=${page}&limit=${limit}${filter}`;
    
    return this.http.get<Invoice[]>(url).pipe(
      map(data => data.map(item => new Invoice().deserialize(item))),
      retry(2),
      catchError(this.handleError)
    );
  }

  // Get invoices data
  getInvoiceList(page, limit, tab?): Observable<Invoice[]> {
    
    // Check if we have a filter and use it if we do
    var filterToSend = '';
    if (tab == 'all')
      filterToSend = '';
    if (tab == 'draft')
      filterToSend = '&isPaid=false&isSent=false';
    if (tab == 'awaiting')
      filterToSend = '&isPaid=false&isSent=true';
    if (tab == 'paid')
      filterToSend = '&isPaid=true';

    return this.http
      .get<Invoice[]>(this.base_path+'invoices?sort=invoice_date&order=DESC&page='+page+'&limit='+limit+'&'+filterToSend)
      .pipe(
        map(data => data.map(data => new Invoice().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      )
  }

  getInvoiceListFiltered(page, limit, query): Observable<Invoice[]> {
    
    // Check if we have a filter and use it if we do
    var filterToSend = 'query='+query; // TODO: Change this to full text search invoices by names

    return this.http
      .get<Invoice[]>(this.base_path+'invoices?sort=invoice_date&order=DESC&page='+page+'&limit='+limit+'&'+filterToSend)
      .pipe(
        map(data => data.map(data => new Invoice().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      )
  }

  getInvoice(id): Observable<Invoice> {
    return this.http
      .get<Invoice>(this.base_path+'invoices/' + id)
      .pipe(
        map(data => new Invoice().deserialize(data)),
        tap(updatedInvoice => {
          this.invoiceService.updateInvoice(updatedInvoice);
        }),
        retry(2),
        catchError(this.handleError)
      )
  }

  createInvoice(item): Observable<Invoice> {
    return this.http
      .post<Invoice>(this.base_path+'invoices', JSON.stringify(item), this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  createInvoiceWithWorkItems(item, enteredItems): Observable<any> {
    return this.createInvoice(item).pipe(
      switchMap((createdInvoice) => {
        const workItems = enteredItems || []; // Ensure WorkItems exists
        if (workItems.length > 0) {
          // Set the invoiceId for each work item and create them
          const workItemObservables = workItems.map((workItem) => {
            workItem.invoiceId = createdInvoice.id; // Set the invoiceId
            return this.createWorkItem(workItem);  // Create each work item
          });
  
          // Use forkJoin to wait for all work items to be created
          return forkJoin(workItemObservables).pipe(
            map((createdWorkItems) => ({
              invoice: createdInvoice,
              workItems: createdWorkItems,
            }))
          );
        } else {
          // If no work items, just return the created invoice
          return of({ invoice: createdInvoice, workItems: [] });
        }
      })
    );
  }

  updateInvoice(id: number, item: any): Observable<Invoice> {
    return this.http
      .patch<Invoice>(`${this.base_path}invoices/${id}`, JSON.stringify(item), this.httpOptions)
      .pipe(
        map(data => new Invoice().deserialize(data)), // Ensure consistent mapping
        tap(updatedInvoice => {
          this.invoiceService.updateInvoice(updatedInvoice);
        }),
        retry(2),
        catchError(this.handleError)
      );
  }

  // Invoice: Delete - the API should ensure that charges are also removed
  deleteInvoice(id) {
    return this.http
      .delete<Invoice>(this.base_path + 'invoices/' + id, this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  // Get invoice data by contact
  getInvoiceListByContact(contactId): Observable<Invoice[]> {
    return this.http
      .get<Invoice[]>(this.base_path+'invoices?sort=invoice_date&order=DESC&contactId='+contactId)
      .pipe(
        map(data => data.map(data => new Invoice().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      );
  }

  // Get invoice data by job
  getInvoiceListByJob(jobId): Observable<Invoice[]> {
    return this.http
      .get<Invoice[]>(this.base_path+'invoices?sort=invoice_date&order=DESC&jobId='+jobId)
      .pipe(
        map(data => data.map(data => new Invoice().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      );
  }

  // Get invoice data for month
  getInvoiceListForMonth(monthKey): Observable<Invoice[]> {
    return this.http
      .get<Invoice[]>(this.base_path+'invoices/month?month='+monthKey)
      .pipe(
        map(data => data.map(data => new Invoice().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      );
  }

  getInvoicePreviewPdf(id): Observable<any> {
    const options = {
      observe: 'response' as 'body',
      Accept: 'application/pdf',
      responseType: 'blob' as 'blob'
    };

    return this.http
      .get(this.base_path+'invoices/' + id + '/pdf', options)
      .pipe(catchError(err => { throw err; }));
  }

  // Preview PDFs (payload → PDF)
  previewInvoicePdf(payload: any): Observable<Blob> {
    return this.http
      .post<Blob>(`${this.base_path}invoices/preview`, payload, { responseType: 'blob' as 'json' })
      .pipe(catchError(this.handleError));
  }

  previewQuotePdf(payload: any): Observable<Blob> {
    return this.http
      .post<Blob>(`${this.base_path}quotes/preview`, payload, { responseType: 'blob' as 'json' })
      .pipe(catchError(this.handleError));
  }

  getInvoiceReport(startDate, endDate): Observable<any> {
    return this.http
      .get<any>(this.base_path+'invoices/report?startDate='+startDate+'&endDate='+endDate+'')
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  getInvoiceReportMonthly(queryParams): Observable<any> {
    const { startDate, endDate, payments } = queryParams

    // if payments is true, include payments in the report else do not include payments parameter
    if (payments === 'false') {
      return this.http
        .get<any>(this.base_path+'invoices/reportmonthly?startDate='+startDate+'&endDate='+endDate)
        .pipe(
          retry(2),
          catchError(this.handleError)
        )
    }
    else {
      return this.http
        .get<any>(this.base_path+'invoices/reportmonthly?startDate='+startDate+'&endDate='+endDate+'&payments=true')
        .pipe(
          retry(2),
          catchError(this.handleError)
        )
    }
  }

  // Invoice Payments
  getInvoicePayments(invoiceId): Observable<Payment[]> {
    return this.http
      .get<any>(this.base_path+'invoices/'+invoiceId+'/payments')
      .pipe(
        map(data => data.map(data => new Payment().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      )
  }

  deletePayment(paymentId) {
    return this.http
      .delete<Payment>(this.base_path + 'payments/' + paymentId, this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  createPayment(newPayment): Observable<Payment> {
    return this.http
      .post<Payment>(this.base_path+'invoices/'+newPayment.invoiceId+'/payments', JSON.stringify(newPayment), this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  // Send Invoice Email
  sendInvoiceEmail(invoiceId, businessId, emailParams): Observable<any> {
    var requestBody = {
      sendTo: emailParams.sendTo,
      subject: emailParams.subject,
      message: emailParams.message,
      businessId: businessId,
      isTextOnly: emailParams.isTextOnly,
      includeInvoiceLink: emailParams.includeInvoiceLink,
      includePDF: emailParams.includePDF,
      includeMe: emailParams.includeMe
    }

    return this.http
      .post<any>(this.base_path+'invoices/'+invoiceId+'/email', JSON.stringify(requestBody), this.httpOptions)
      .pipe(
        catchError(this.handleError)
      )
  }

  createInvoiceFromJob(businessId, contactId, jobId, propertyId): Observable<Invoice> {
    return this.http
      .post<Invoice>(this.base_path+'invoices', { businessId: businessId, contactId: contactId, jobId: jobId, propertyId: propertyId }, this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }
  
  /*
   * Quotes
   */

  createQuote(item): Observable<Quote> {
    return this.http
      .post<Quote>(this.base_path+'quotes', JSON.stringify(item), this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  createQuoteWithWorkItems(item, enteredItems): Observable<any> {
    return this.createQuote(item).pipe(
      switchMap((createdQuote) => {
        const workItems = enteredItems || []; // Ensure WorkItems exists
        if (workItems.length > 0) {
          // Set the quoteId for each work item and create them
          const workItemObservables = workItems.map((workItem) => {
            workItem.quoteId = createdQuote.id; // Set the quoteId
            return this.createWorkItem(workItem);  // Create each work item
          });
  
          // Use forkJoin to wait for all work items to be created
          return forkJoin(workItemObservables).pipe(
            map((createdWorkItems) => ({
              quote: createdQuote,
              workItems: createdWorkItems,
            }))
          );
        } else {
          // If no work items, just return the created quote
          return of({ quote: createdQuote, workItems: [] });
        }
      })
    );
  }

  copyQuoteToInvoice(quoteId, jobId): Observable<Invoice> {
    return this.http
      .post<Invoice>(this.base_path+'invoices/copy-from-quote/'+quoteId, { jobId: jobId }, this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  createQuoteFromJob(businessId, contactId, jobId, propertyId): Observable<Job> {
    return this.http
      .post<Quote>(this.base_path + 'quotes', { businessId, contactId, jobId, propertyId }, this.httpOptions)
      .pipe(
        retry(2),
        switchMap(quote => {
          // Assuming the quote object contains the new quote ID
          if (!quote.id) {
            return throwError('Failed to create quote. No ID returned.');
          }
          // Update the job with the new quote ID
          return this.updateJob(jobId, { quoteId: quote.id });
        }),
        catchError(this.handleError)
      );
  }

  getQuotesMaster(page: number, limit: number, tab?: string, query?: string): Observable<Quote[]> {
    let filter = '';
    
    // Handle tab-based filters
    if (tab) {
      switch (tab) {
        case 'all':
          filter += ''; // No additional filter needed
          break;
        case 'draft':
          filter += '&isSent=false&status=NONE';
          break;
        case 'awaiting':
          filter += '&isSent=true&status=NONE';
          break;
        case 'accepted':
          filter += '&status=ACCEPTED';
          break;
        case 'rejected':
          filter += '&status=REJECTED';
          break;
      }
    }
    
    // Handle search query
    if (query) {
      filter += `&query=${encodeURIComponent(query)}`; // Ensure query is URL encoded
    }

    const url = `${this.base_path}quotes?sort=quote_date&order=DESC&page=${page}&limit=${limit}${filter}`;
    
    return this.http.get<Quote[]>(url).pipe(
      map(data => data.map(item => new Quote().deserialize(item))),
      retry(2),
      catchError(this.handleError)
    );
  }

  getQuoteList(page, limit, tab?): Observable<Quote[]> {
    
    // Check if we have a filter and use it if we do
    var filterToSend = '';
    if (tab == 'all')
      filterToSend = '';
    if (tab == 'draft')
      filterToSend = 'isSent=false&isAccepted=false';
    if (tab == 'awaiting')
      filterToSend = 'isSent=true&isAccepted=false';
    if (tab == 'accepted')
      filterToSend = 'isAccepted=true';

    return this.http
      .get<Quote[]>(this.base_path+'quotes?sort=quote_date&order=DESC&page='+page+'&limit='+limit+'&'+filterToSend)
      .pipe(
        map(data => data.map(data => new Quote().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      )
  }

  getQuoteListFiltered(page, limit, query): Observable<Quote[]> {
    
    // Check if we have a filter and use it if we do
    var filterToSend = 'query='+query; // TODO: Change this to full text search quotes by names

    return this.http
      .get<Quote[]>(this.base_path+'quotes?sort=quote_date&order=DESC&page='+page+'&limit='+limit+'&'+filterToSend)
      .pipe(
        map(data => data.map(data => new Quote().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      )
  }

  getQuote(id): Observable<Quote> {
    return this.http
      .get<Quote>(this.base_path+'quotes/' + id)
      .pipe(
        map(data => new Quote().deserialize(data)),
        tap(updatedQuote => {
          this.quoteService.updateQuote(updatedQuote);
        }),
        retry(2),
        catchError(this.handleError)
      )
  }

  updateQuote(id, item): Observable<Quote> {
    console.log(JSON.stringify(item));
    return this.http
      .patch<Quote>(this.base_path+'quotes/' + id, JSON.stringify(item), this.httpOptions)
      .pipe(
        map(data => new Quote().deserialize(data)), // Ensure consistent mapping
        tap(updatedQuote => {
          this.quoteService.updateQuote(updatedQuote);
        }),
        retry(2),
        catchError(this.handleError)
      );
  }

  deleteQuote(id) {
    return this.http
      .delete<Quote>(this.base_path + 'quotes/' + id, this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  // Get quote data by contact
  getQuoteListByContact(contactId): Observable<Quote[]> {
    return this.http
      .get<Quote[]>(this.base_path+'quotes?sort=quote_date&order=DESC&contactId='+contactId)
      .pipe(
        map(data => data.map(data => new Quote().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      );
  }

  getQuotePreviewPdf(id): Observable<any> {
    const options = {
      observe: 'response' as 'body',
      Accept: 'application/pdf',
      responseType: 'blob' as 'blob'
    };

    return this.http
      .get(this.base_path+'quotes/' + id + '/pdf', options)
      .pipe(catchError(err => { throw err; }));
  }

  getQuoteReport(startDate, endDate): Observable<any> {
    return this.http
      .get<any>(this.base_path+'quotes/report?startDate='+startDate+'&endDate='+endDate+'')
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  getQuoteReportMonthly(queryParams): Observable<any> {
    const { startDate, endDate } = queryParams;
    return this.http
      .get<any>(this.base_path+'quotes/reportmonthly?startDate='+startDate+'&endDate='+endDate)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  // Get quotes data for month, optionally filtered by status (ACCEPTED | REJECTED | NONE)
  getQuotesListForMonth(monthKey: string, status?: 'ACCEPTED'|'REJECTED'|'NONE'): Observable<Quote[]> {
    const statusParam = status ? '&status='+encodeURIComponent(status) : '';
    return this.http
      .get<Quote[]>(this.base_path+'quotes/month?month='+encodeURIComponent(monthKey)+statusParam)
      .pipe(
        map(data => data.map(data => new Quote().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      );
  }

  // Send Quote Email
  sendQuoteEmail(quoteId, businessId, emailParams): Observable<any> {
    var requestBody = {
      sendTo: emailParams.sendTo,
      subject: emailParams.subject,
      message: emailParams.message,
      businessId: businessId,
      isTextOnly: emailParams.isTextOnly,
      includeQuoteLink: emailParams.includeQuoteLink,
      includePDF: emailParams.includePDF,
      includeMe: emailParams.includeMe
    }

    return this.http
      .post<any>(this.base_path+'quotes/'+quoteId+'/email', JSON.stringify(requestBody), this.httpOptions)
      .pipe(
        catchError(this.handleError)
      )
  }

  createJobFromQuote(quoteId, contactId, businessId): Observable<Job> {
    return this.http
      .post<Job>(this.base_path+'jobs', JSON.stringify({ contactId: contactId, businessId: businessId, quoteId: quoteId }), this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }
  
  /* 
   * Appointments
   */

  getAppointments() {
    return this.http
      .get<Appointment[]>(this.base_path+'appointments')
      .pipe(
        map(data => data.map(data => new Appointment().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      );
  }

  // Get appointment
  getAppointment(id): Observable<Appointment> {
    return this.http
      .get<Appointment>(this.base_path+'appointments/' + id)
      .pipe(
        map(data => new Appointment().deserialize(data)),
        retry(2),
        catchError(this.handleError)
      )
  }

  getAppointmentsByRange(startTime, endTime) {
    return this.http
      .get<Appointment[]>(this.base_path+'appointments?startTime_gte='+startTime+'&startTime_lte='+endTime+'&_expand=contact')
      .pipe(
        map(data => data.map(data => new Appointment().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      );
  }

  getRemindersByRange(startTime, endTime) {
    return this.http
      .get<Appointment[]>(this.base_path+'appointments/reminders?startTime_gte='+startTime+'&startTime_lte='+endTime)
      .pipe(
        map(data => data.map(data => new Appointment().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      );
  }

  createAppointment(item): Observable<Appointment> {
    return this.http
      .post<Appointment>(this.base_path+'appointments', JSON.stringify(item), this.httpOptions)
      .pipe(
        map(data => new Appointment().deserialize(data)),
        tap(updatedAppointment => {
          this.appointmentService.updateAppointment(updatedAppointment);
        }),
        retry(2),
        catchError(this.handleError)
      )
  }

  deleteAppointment(id) {
    return this.http
      .delete<Appointment>(this.base_path + 'appointments/' + id, this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  updateAppointment(id, item): Observable<Appointment> {
    return this.http
      .patch<Appointment>(this.base_path + 'appointments/' + id, JSON.stringify(item), this.httpOptions)
      .pipe(
        map(data => new Appointment().deserialize(data)),
        tap(updatedAppointment => {
          this.appointmentService.updateAppointment(updatedAppointment);
        }),
        retry(2),
        catchError(this.handleError)
      )
  }

  /* Work Item */

  createWorkItem(item): Observable<WorkItem> {
    return this.http
      .post<WorkItem>(this.base_path+'workitems', JSON.stringify(item), this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  getRecentWorkItems(limit: number = 20): Observable<WorkItem[]> {
    return this.http
      .get<WorkItem[]>(this.base_path+`workitems?sort=createdAt&order=DESC&limit=${limit}`)
      .pipe(
        map(data => data.map(data => new WorkItem().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      );
  }

  updateWorkItem(id, item): Observable<WorkItem> {
    return this.http
      .patch<WorkItem>(this.base_path+'workitems/' + id, JSON.stringify(item), this.httpOptions)
      .pipe(
        map(data => new WorkItem().deserialize(data)),
        retry(2),
        catchError(this.handleError)
      )
  }

  deleteWorkItem(id): Observable<WorkItem> {
    return this.http
      .delete<WorkItem>(this.base_path+'workitems/' + id, this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  // UPDATE WORK ITEM ORDER
  updateWorkItemOrder(workItemId: number, order: number): Observable<WorkItem> {
    return this.http
      .patch<WorkItem>(this.base_path+'workitems/' + workItemId, { order: order }, this.httpOptions)
      .pipe(
        map(data => new WorkItem().deserialize(data)),
        retry(2),
        catchError(this.handleError)
      )
  }

  /* 
   * Businesses
   */

  // Get businesses data
  getBusinessList(): Observable<Business[]> {
    return this.http
      .get<Business[]>(this.base_path+'businesses/')
      .pipe(
        map(data => data.map(data => new Business().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      );
  }

  createBusiness(item): Observable<Business> {
    return this.http
      .post<Business>(this.base_path+'businesses', JSON.stringify(item), this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  getBusiness(id): Observable<Business> {
    return this.http
      .get<Business>(this.base_path+'businesses/' + id)
      .pipe(
        map(data => new Business().deserialize(data)),
        retry(2),
        catchError(this.handleError)
      )
  }

  updateBusiness(id, data): Observable<Business> {
    return this.http
      .patch<Business>(this.base_path+'businesses/' + id, data, this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  deleteBusiness(id) {
    return this.http
      .delete<Business>(this.base_path + 'businesses/' + id, this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  uploadBusinessLogo(businessId: string, accountId: string, formData: FormData): Observable<Business> {

    // Upload Account File to current account
    // Ensure type so backend stores under account-files/<accountId>/logos/
    try { formData.append('type', 'logos'); } catch (e) { /* ignore duplicate */ }
    return this.http.post(this.base_path+'accounts/' + accountId + '/upload-file', formData).pipe(
      switchMap((res) => {
        console.log(res);
  
        var newLogoId = res['id'];
  
        // Return the HTTP API Call to update the business logoId
        return this.http.patch<Business>(this.base_path+'businesses/' + businessId, { logoId: newLogoId}).pipe(
          retry(2),
          catchError(this.handleError)
        );
      }),
      catchError(this.handleError)
    );
  }

  getAccountFileBinary(accountId: number, fileId: number): Observable<Blob> {
    const endpoint = `${this.base_path}accounts/${accountId}/files/${fileId}/binary`;
    return this.http.get(endpoint, { responseType: 'blob' }); // Set responseType to 'blob' to tell HttpClient you expect a binary response
  }

  getAccountFileBinaryThumb(accountId: number, fileId: number): Observable<Blob> {
    const endpoint = `${this.base_path}accounts/${accountId}/files/${fileId}/binary?height=240`;
    return this.http.get(endpoint, { responseType: 'blob' }); // Set responseType to 'blob' to tell HttpClient you expect a binary response
  }

  deleteAccountFile(accountId: number, fileId: number): Observable<any> { 
    return this.http.delete(this.base_path+'accounts/' + accountId + '/files/' + fileId);
  }

  uploadAccountFile(accountId: string, formData: FormData): Observable<any> {
    // Caller may set a specific type; if not set, default to 'misc'
    if (!(formData as any).has || !(formData as any).has('type')) {
      try { formData.append('type', 'misc'); } catch (e) { /* ignore */ }
    }
    return this.http
      .post<any>(this.base_path + 'accounts/' + accountId + '/upload-file', formData)
      .pipe(
        retry(2),
        catchError(this.handleError)
      );
  }

  /* 
   * JOBS
   */

  // Get jobs data
  getJobList(page, limit, tab?, statusTab?): Observable<Job[]> {
    
    // Check if we have a filter and use it if we do
    var filterToSend = '';
    if (tab == 'closed')
      filterToSend = '&isOpen=false';
    if (tab == 'open' && statusTab == 'ALL')
      filterToSend = '&isOpen=true';
    if (tab == 'open' && statusTab == 'NEW')
      filterToSend = '&isOpen=true&status=NEW';
    if (tab == 'open' && statusTab == 'SCHEDULED')
      filterToSend = '&isOpen=true&status=SCHEDULED';
    if (tab == 'open' && statusTab == 'COMPLETED')
      filterToSend = '&isOpen=true&status=COMPLETED';

    return this.http
      .get<Invoice[]>(this.base_path+'jobs?sort=createdAt&order=DESC&page='+page+'&limit='+limit+'&'+filterToSend)
      .pipe(
        map(data => data.map(data => new Job().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      )
  }

  // Get Jobs Overview
  getJobOverview(): Observable<Job[]> {
    return this.http
      .get<any>(this.base_path+'jobs/overview')
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  getJobListFiltered(page, limit, query): Observable<Job[]> {
    
    // Check if we have a filter and use it if we do
    var filterToSend = 'query='+query;

    return this.http
      .get<Job[]>(this.base_path+'jobs?sort=createdAt&order=DESC&page='+page+'&limit='+limit+'&'+filterToSend)
      .pipe(
        map(data => data.map(data => new Job().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      )
  }

  getJob(id): Observable<Job> {
    return this.http
      .get<Job>(this.base_path+'jobs/' + id + '')
      .pipe(
        map(data => new Job().deserialize(data)),
        tap(updatedJob => {
          this.jobService.updateJob(updatedJob);
        }),
        retry(2),
        catchError(this.handleError)
      )
  }

  createJob(item): Observable<Job> {
    return this.http
      .post<Job>(this.base_path+'jobs', JSON.stringify(item), this.httpOptions)
      .pipe(
        map(data => new Job().deserialize(data)),
        tap(newJob => {
          this.jobService.updateJob(newJob);
        }),
        retry(2),
        catchError(this.handleError)
      )
  }

  updateJob(id, item): Observable<Job> {
    console.log(JSON.stringify(item));
    return this.http
      .patch<Job>(this.base_path+'jobs/' + id, JSON.stringify(item), this.httpOptions)
      .pipe(
        map(data => new Job().deserialize(data)), // Ensure consistent mapping
        tap(updatedJob => {
          this.jobService.updateJob(updatedJob);
        }),
        retry(2),
        catchError(this.handleError)
      );
  }

  // Job: Delete
  deleteJob(id) {
    return this.http
      .delete<Job>(this.base_path + 'jobs/' + id + '?deleteMode=removeReferences', this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  // Get job data by contact
  getJobListByContact(contactId, page, limit): Observable<Job[]> {
    var pageToSend = page || 1;
    var limitToSend = limit || 50;

    return this.http
      .get<Job[]>(this.base_path+'jobs?sort=createdAt&order=DESC&contactId='+contactId+'&page'+pageToSend+'&limit='+limitToSend)
      .pipe(
        map(data => data.map(data => new Job().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      );
  }

  /* Auth */
  login(email, password): Observable<any> {
    var credentials = {
      email: email,
      password: password
    }

    return this.http
      .post<any>(this.base_path+'auth/login', JSON.stringify(credentials), this.noAccountHttpOptions)
      .pipe(
        catchError(this.handleError)
      )
  }

  sendResetPasswordEmail(email): Observable<any> {
    var credentials = {
      email: email
    }

    return this.http
      .post<any>(this.base_path+'auth/forgot-password', JSON.stringify(credentials), this.noAccountHttpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  // Change password by sending the old password and new password
  changePassword(oldPassword, newPassword): Observable<any> {
    var credentials = {
      oldPassword: oldPassword,
      newPassword: newPassword
    }

    return this.http
      .post<any>(this.base_path+'auth/change-password', JSON.stringify(credentials), this.httpOptions)
  }

  /* Accounts */
  createAccount(name: string, country?: string, currency?: string): Observable<any> {
    const body: any = { name };
    if (country) body.country = country;
    if (currency) body.currency = currency;
    return this.http
      .post<any>(this.base_path+'accounts', JSON.stringify(body), this.noAccountHttpOptions)
      .pipe(
        catchError(this.handleError)
      )
  }

  /* Countries */
  getCountries(): Observable<any[]> {
    return this.http
      .get<any[]>(this.base_path+'countries', this.noAccountHttpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      );
  }

  deleteAccount(accountId: string): Observable<any> {
    return this.http
      .delete<any>(this.base_path+'accounts/' + accountId, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      )
  }

  deleteUser(): Observable<any> {
    return this.http
      .delete<any>(this.base_path+'auth/delete', this.httpOptions)
      .pipe(
        catchError(this.handleError)
      )
  }

  createUserAccount(email: string, password: string, name: string): Observable<any> {
    const body = { email, password, name };
    console.log('I am here')
    return this.http
      .post<any>(this.base_path+'auth/signup', JSON.stringify(body), this.noAccountHttpOptions)
      .pipe(
        catchError(this.handleError)
      )
  }

  workitemGeneration(businessId:number, jobDescription:string): Observable<WorkItem[]> {
    const body = { jobDescription };
    return this.http
      .post<any>(this.base_path+'businesses/' + businessId + '/generate-work-items', JSON.stringify(body), this.httpOptions)
      .pipe(
        map(data => data.data.map(data => new WorkItem().deserialize(data))),
        catchError(this.handleError)
      )
  }

  // Business Payment Details
  getBusinessPaymentDetails(businessId): Observable<BusinessPaymentDetails[]> {
    return this.http
      .get<BusinessPaymentDetails[]>(this.base_path+'businesses/' + businessId + '/payment-details')
      .pipe(
        map(data => data.map(data => new BusinessPaymentDetails().deserialize(data))),
        retry(2),
        catchError(this.handleError)
      );
  }

  getBusinessPaymentDetailsById(businessId, paymentDetailsId): Observable<BusinessPaymentDetails> {
    return this.http
      .get<BusinessPaymentDetails>(this.base_path+'businesses/' + businessId + '/payment-details/' + paymentDetailsId)
      .pipe(
        map(data => new BusinessPaymentDetails().deserialize(data)),
        retry(2),
        catchError(this.handleError)
      );
  }

  deleteBusinessPaymentDetails(businessId, paymentDetailsId) {
    return this.http
      .delete<BusinessPaymentDetails>(this.base_path + 'businesses/' + businessId + '/payment-details/' + paymentDetailsId, this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  addBusinessPaymentDetails(businessId, paymentDetails): Observable<BusinessPaymentDetails> {
    return this.http
      .post<BusinessPaymentDetails>(this.base_path+'businesses/' + businessId + '/payment-details', JSON.stringify(paymentDetails), this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  editBusinessPaymentDetails(businessId, paymentDetailsId, paymentDetails): Observable<BusinessPaymentDetails> {
    return this.http
      .patch<BusinessPaymentDetails>(this.base_path+'businesses/' + businessId + '/payment-details/' + paymentDetailsId, JSON.stringify(paymentDetails), this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  updateBusinessPaymentDetailsIsActive(businessId, paymentDetailsId, isActive): Observable<BusinessPaymentDetails> {
    return this.http
      .patch<BusinessPaymentDetails>(this.base_path+'businesses/' + businessId + '/payment-details/' + paymentDetailsId, { isActive: isActive }, this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      )
  }

  /* 
   * GALLERIES
   */

  getGalleries(page: number = 1, limit: number = 10): Observable<any[]> {
    return this.http
      .get<any[]>(this.base_path + 'galleries?sort=createdAt&order=DESC&page=' + page + '&limit=' + limit)
      .pipe(
        retry(2),
        catchError(this.handleError)
      );
  }

  getGallery(id: string): Observable<any> {
    return this.http
      .get<any>(this.base_path + 'galleries/' + id)
      .pipe(
        retry(2),
        catchError(this.handleError)
      );
  }

  createGallery(gallery: any): Observable<any> {
    return this.http
      .post<any>(this.base_path + 'galleries', gallery)
      .pipe(
        retry(2),
        catchError(this.handleError)
      );
  }

  deleteGallery(id: string): Observable<void> {
    return this.http
      .delete<void>(this.base_path + 'galleries/' + id)
      .pipe(
        retry(2),
        catchError(this.handleError)
      );
  }

  getGalleryPhotos(galleryId: string): Observable<any[]> {
    return this.http
      .get<any[]>(this.base_path + 'galleries/' + galleryId + '/photos')
      .pipe(
        retry(2),
        catchError(this.handleError)
      );
  }

  uploadGalleryPhotos(galleryId: string, formData: FormData): Observable<any> {
    return this.http
      .post<any>(this.base_path + 'galleries/' + galleryId + '/photos', formData)
      .pipe(
        retry(2),
        catchError(this.handleError)
      );
  }

  deleteGalleryPhoto(galleryId: string, photoId: string): Observable<any> {
    return this.http
      .delete<any>(this.base_path + 'galleries/' + galleryId + '/photos/' + photoId, this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      );
  }

  createGalleryPhoto(galleryId: string, data: { accountFileId: number }): Observable<any> {
    return this.http.post(this.base_path + 'galleries/' + galleryId + '/photos', JSON.stringify(data), this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      );
  }

  updateGallery(galleryId: string, data: any): Observable<any> {
    return this.http.patch(`${this.base_path}galleries/${galleryId}`, data);
  }

  // Stripe Connect related methods
  createStripeConnectAccount(businessId: number, platform?: 'mobile' | 'web'): Observable<StripeConnectOnboardingResponse> {
    const platformParam = platform === 'mobile' ? '?platform=mobile' : '';
    return this.http.post<StripeConnectOnboardingResponse>(`${this.base_path}businesses/${businessId}/stripe-connect${platformParam}`, {});
  }

  createStripeConnectOAuth(businessId: number, platform?: 'mobile' | 'web'): Observable<StripeConnectOnboardingResponse> {
    const suffix = platform === 'mobile' ? '?mode=oauth&platform=mobile' : '?mode=oauth';
    return this.http.post<StripeConnectOnboardingResponse>(`${this.base_path}businesses/${businessId}/stripe-connect${suffix}`, {});
  }

  getBusinessStripeAccount(businessId: number): Observable<StripeConnectAccountResponse> {
    return this.http.get<StripeConnectAccountResponse>(`${this.base_path}businesses/${businessId}/stripe-connect`);
  }

  getStripeDashboardLink(businessId: number, platform?: 'mobile' | 'web'): Observable<{ url: string }> {
    const suffix = platform === 'mobile' ? '?platform=mobile' : '';
    return this.http.get<{ url: string }>(`${this.base_path}businesses/${businessId}/stripe-connect/dashboard${suffix}`);
  }

  disconnectStripeAccount(businessId: number): Observable<any> {
    return this.http.delete(`${this.base_path}businesses/${businessId}/stripe-connect`);
  }

  /*
   * USER PROFILE
   */
  updateUser(userId: number, data: Partial<User>): Observable<User> {
    return this.http
      .patch<User>(`${this.base_path}users/${userId}`, JSON.stringify(data), this.httpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      );
  }

  updateCurrentUser(data: Partial<User>): Observable<User> {
    return this.http
      .patch<User>(`${this.base_path}auth/current`, JSON.stringify(data), this.noAccountHttpOptions)
      .pipe(
        retry(2),
        catchError(this.handleError)
      );
  }

}